/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

import { makeObservable, observable, runInAction } from "mobx"
import { AxiosError } from "axios"
import MedaicaApiService, { isMedaicaApiError, MedaicaApiErrorType } from "@medaica/common/services/medaica-api-service"
import { isReportGone } from "@medaica/common/services/medaica-api-service/PatientReportApi"
import AccessTokenStorage from "@medaica/common/services/access-token-storage"
import { PatientReportFacts } from "@medaica/common/types"
import { logError } from "@medaica/common/services/util"
import downloadBlob from "@medaica/common/utils/download-blob"
import { addSeconds } from "date-fns"
import createPatientReportApi from "../api/create-patient-report-api"
import { PatientReportApiClient } from "../api/patient-report-api-client"
import HeartReportUiStore from "./heart-report-ui-store"

type UnavailableReason = "expired" | "unavailable"
type RequestCodeNotice = "sessionEnded" | "codeUsed"

const SESSION_STORAGE_KEY = "session"
// The download session the API mints is short-lived (15 minutes); the page also ends it
// locally at the same moment, so the download screen never lingers past the token.
const SESSION_TTL_SECONDS = 15 * 60

type ApiErrorShape = { type?: MedaicaApiErrorType; reason?: unknown; response?: { status?: number } }

const errorShape = (error: unknown): ApiErrorShape => error as ApiErrorShape

// A dead link, either at the gate (400) or mid-session (410 once the release was reverted).
const isDeadLink = (error: unknown): boolean =>
  isReportGone(error) ||
  (isMedaicaApiError(error) && errorShape(error).type === MedaicaApiErrorType.INVALID_REPORT_LINK)

// A 410 only ever means the report was withdrawn, and a PDF call cannot say so itself: its
// response is read as a blob, so the error body's reason never reaches the page.
const deadLinkReason = (error: unknown): UnavailableReason =>
  isReportGone(error) || errorShape(error).reason === "unavailable" ? "unavailable" : "expired"

// The download session is over (expired or otherwise not accepted): any 401, or the typed
// expiry errors the API uses for its short-lived session token.
const isSessionEnded = (error: unknown): boolean => {
  const { type } = errorShape(error)
  return (
    (error as AxiosError).response?.status === 401 ||
    (isMedaicaApiError(error) &&
      (type === MedaicaApiErrorType.SIGNATURE_HAS_EXPIRED ||
        type === MedaicaApiErrorType.EXPIRED_SESSION ||
        type === MedaicaApiErrorType.UNAUTHORIZED))
  )
}

/**
 * Owns the code gate and the short download session for the patient Medaica Report
 * page, modeled on the booking wizard's session store. The link token from the
 * emailed URL is read once by the page and handed in here; nothing is sent to the
 * API until the patient asks for a code (email security scanners open links, and an
 * automatic send would burn the patient's send allowance). After a correct code the
 * store holds the 15-minute session token (persisted tab-scoped so a refresh of the
 * same link lands back on the download screen) and loads the report's header facts.
 */
class HeartReportSessionStore {
  readonly token: string | null
  maskedEmail: string | null = null
  verificationToken: string | null = null
  codeExpiresAt: Date | null = null
  session: { token: string; expiresAt: Date } | null = null
  facts: PatientReportFacts | null = null
  unavailableReason: UnavailableReason | null = null
  // Explains, on the landing screen, why the patient is back there (e.g. the session ended).
  notice: RequestCodeNotice | null = null

  readonly api: PatientReportApiClient
  private readonly _uiStore: HeartReportUiStore
  // sessionStorage (not localStorage): the download session token is tab-scoped and dies
  // with the tab, so it can't linger on shared computers. F5 in the same tab still restores.
  private readonly _storage = new AccessTokenStorage("PATIENT_REPORT_TOKENS", sessionStorage)
  private _expiryTimer: ReturnType<typeof setTimeout> | null = null

  constructor(uiStore: HeartReportUiStore, token: string | null) {
    this._uiStore = uiStore
    this.token = token
    // A private, token-closure API instance — never the global Auth0-wired service — so the
    // Bearer header carries the download session token and nothing else.
    this.api = createPatientReportApi(
      () => new MedaicaApiService(() => Promise.resolve(this.session?.token)).patientReport
    )

    makeObservable(this, {
      maskedEmail: observable,
      verificationToken: observable,
      codeExpiresAt: observable,
      session: observable,
      facts: observable,
      unavailableReason: observable,
      notice: observable,
    })
  }

  // Time-dependent, so a plain getter (not a cached computed).
  get isAuthenticated(): boolean {
    return !!this.session && new Date() < this.session.expiresAt
  }

  /**
   * Pick the first screen. No token in the URL is a dead link. Otherwise a session cached in
   * this tab is restored ONLY if it was minted for this same link: a different `?t=` (another
   * patient's link on a shared device) evicts whatever was cached and starts at the landing.
   * Nothing is sent to the API here.
   */
  async init(): Promise<void> {
    if (!this.token) {
      this.markUnavailable("expired")
      return
    }
    if (this.restoreSession(this.token)) {
      await this.loadFacts()
      if (this.isAuthenticated && this.facts) {
        this._uiStore.goTo("download")
      }
      return
    }
    this._uiStore.goTo("requestCode")
  }

  private restoreSession(token: string): boolean {
    const stored = this._storage.get(SESSION_STORAGE_KEY)
    // AccessTokenStorage round-trips through JSON and its reviver only revives Maps, so
    // expiresAt comes back as an ISO string — re-hydrate to a Date before comparing.
    // The stored `userId` is the link token the session was minted for.
    if (stored?.userId === token && new Date() < new Date(stored.expiresAt)) {
      runInAction(() => {
        this.session = { token: stored.value, expiresAt: new Date(stored.expiresAt) }
      })
      this.scheduleLocalExpiry()
      return true
    }
    this.clearSession()
    return false
  }

  private persistSession(): void {
    if (this.session && this.token) {
      this._storage.set(SESSION_STORAGE_KEY, {
        value: this.session.token,
        expiresAt: this.session.expiresAt,
        userId: this.token,
      })
    }
  }

  // Evict the session from memory AND storage, so nothing behind the gate (the session
  // token or the loaded facts) survives for the next visit on this device.
  private clearSession(): void {
    this.cancelLocalExpiry()
    runInAction(() => {
      this.session = null
      this.facts = null
    })
    this._storage.remove(SESSION_STORAGE_KEY)
  }

  // End the download screen the moment the session token would stop being accepted, even
  // if the patient just leaves the tab open — the 15-minute limit is a promise on the page.
  private scheduleLocalExpiry(): void {
    this.cancelLocalExpiry()
    if (!this.session) {
      return
    }
    const remainingMs = Math.max(0, this.session.expiresAt.getTime() - Date.now())
    this._expiryTimer = setTimeout(() => this.endSession(), remainingMs)
  }

  private cancelLocalExpiry(): void {
    if (this._expiryTimer) {
      clearTimeout(this._expiryTimer)
      this._expiryTimer = null
    }
  }

  private markUnavailable(reason: UnavailableReason): void {
    this.clearSession()
    runInAction(() => {
      this.unavailableReason = reason
    })
    this._uiStore.goTo("unavailable")
  }

  /** Back to the landing screen with a "your session ended" line; the next code starts fresh. */
  endSession(): void {
    this.clearSession()
    runInAction(() => {
      this.notice = "sessionEnded"
    })
    this._uiStore.goTo("requestCode")
  }

  /**
   * "Send me a code": resolve the link and send the code. A fresh code supersedes any
   * session cached in this browser, so it is evicted first. A dead link routes to the
   * unavailable screen (not thrown); a send failure propagates for the screen to show.
   */
  async sendCode(): Promise<void> {
    if (!this.token) {
      this.markUnavailable("expired")
      return
    }
    this.clearSession()
    try {
      const result = await this.api.createSession(this.token)
      runInAction(() => {
        this.maskedEmail = result.maskedEmail
        this.verificationToken = result.verificationToken
        this.codeExpiresAt = result.codeExpiresAt
        this.notice = null
      })
      this._uiStore.goTo("verify")
    } catch (error) {
      if (isDeadLink(error)) {
        this.markUnavailable(deadLinkReason(error))
        return
      }
      throw error
    }
  }

  /**
   * Mint a fresh code on the same handle (attempts reset server-side). Once the code has
   * been used there is nothing to resend, so the page starts over from the landing.
   */
  async resendCode(): Promise<void> {
    if (!this.verificationToken) {
      return
    }
    try {
      const result = await this.api.resendCode(this.verificationToken)
      runInAction(() => {
        this.codeExpiresAt = result.codeExpiresAt
      })
    } catch (error) {
      if (isMedaicaApiError(error) && errorShape(error).type === MedaicaApiErrorType.CODE_EXPIRED) {
        runInAction(() => {
          this.verificationToken = null
          this.notice = "codeUsed"
        })
        this._uiStore.goTo("requestCode")
        return
      }
      throw error
    }
  }

  /** Verify the code, open the session and load the facts; wrong/expired/locked codes throw. */
  async submitCode(code: string): Promise<void> {
    if (!this.verificationToken) {
      throw new Error("No verification is in progress")
    }
    const result = await this.api.verify(this.verificationToken, code)
    runInAction(() => {
      // Trust the API's lifetime but never exceed the page's own 15-minute promise.
      const ttlSeconds = Math.min(result.expiresIn, SESSION_TTL_SECONDS)
      this.session = { token: result.accessToken, expiresAt: addSeconds(new Date(), ttlSeconds) }
      this.verificationToken = null
    })
    this.persistSession()
    this.scheduleLocalExpiry()
    await this.loadFacts()
    // loadFacts can find the session already gone (or the report withdrawn) and route
    // elsewhere itself; only land on the download screen if it survived.
    if (this.isAuthenticated && this.facts) {
      this._uiStore.goTo("download")
    }
  }

  /** The download screen's header facts. Errors are logged, never thrown (the screen retries). */
  async loadFacts(): Promise<void> {
    try {
      const facts = await this.runSessionCall(() => this.api.getFacts())
      if (facts) {
        runInAction(() => {
          this.facts = facts
        })
      }
    } catch (error) {
      logError(error)
    }
  }

  /** Fetch the PDF with the session token and hand it to the browser as a file. */
  async downloadPdf(): Promise<void> {
    const filename = this.facts?.filename
    const blob = await this.runSessionCall(() => this.api.getPdf())
    if (blob && filename) {
      downloadBlob(blob, filename)
    }
  }

  /**
   * Fetch the clinician-format copy (the one for the patient's own doctor) and hand it to the
   * browser under its own filename. Does nothing when the report comes without one.
   */
  async downloadProviderPdf(): Promise<void> {
    const filename = this.facts?.providerReport?.filename
    if (!filename) {
      return
    }
    const blob = await this.runSessionCall(() => this.api.getProviderPdf())
    if (blob) {
      downloadBlob(blob, filename)
    }
  }

  /**
   * Wrap a session-scoped API call. A session that has ended (locally or per the API)
   * returns the patient to the landing screen with a "session ended" line; a withdrawn
   * report (410) shows the unavailable screen. Both return null; other errors propagate.
   */
  async runSessionCall<T>(call: () => Promise<T>): Promise<T | null> {
    if (!this.isAuthenticated) {
      this.endSession()
      return null
    }
    try {
      return await call()
    } catch (error) {
      if (isDeadLink(error)) {
        this.markUnavailable(deadLinkReason(error))
        return null
      }
      if (isSessionEnded(error)) {
        this.endSession()
        return null
      }
      throw error
    }
  }

  /** Release the local expiry timer when the page unmounts. */
  dispose(): void {
    this.cancelLocalExpiry()
  }
}

export default HeartReportSessionStore
export type { UnavailableReason, RequestCodeNotice }
